import { apiErrorMessage } from "utils/apiError"
import { enqueueSnackbar } from "notistack"
import { api } from "../../api"
import { setAlertFunc } from "../../components/AlertToaster/AlertSlice"
import {
    DropdownObj,
    HistoryResponse,
    IFile,
    IOtherDetailItem,
    IResponse,
    SiteIdObj,
    StatusButton,
    TicketDataResponseType,
} from "../Common/commonTypes"
import { setLogisticsDealDetailsData } from "./LogisticsSlice"

const MODULE_URL = `logistics`

export const JOB_WORK_ID_EDITABLE_STATUSES = ["open", "dispatch initiated"]

type ticket_obj = {
    display_id: string
    assigned_to: string | number
    status: string
}

type OpsObj = ticket_obj & {
    name: string
    ticket_id: number
}

type FinanceObj = ticket_obj & {
    fin_ticket_id: number
}

type ShipmentFile = {
    doc_id: number | null
    doc_type: string
    file_name: string
    is_excel: boolean
    is_image: boolean
    name: string
    urls: {
        download: string
        view: string
    }
}

type TicketDetails = {
    project_id: number
    ticket_id: number
    tickets: IOtherDetailItem[]
    total: number
}

export type ShipmentDetails = {
    bill_date: string | null
    bill_number: string | null
    eway_bill_number: string
    eway_file: ShipmentFile | null
    logistics_cost: number | null
    shipment_id: number
    shipment_via: "surface" | "air"
    tracking_id: string
    transporter_id: number
    transporter_name: string
    truck_number: string
    gst_applied: number | null
}

export type DeliveryDocument = {
    amount: string
    document_id: number
    file: ShipmentFile | null
    finance_id: number
    gst: string
    sent_on: string | null
    sent_on_date: string | null
    sent_on_number: string
}

export type ShipmentPayload = FormData

export type OtherDetailsResponseObj = {
    deal_obj: {
        client_poc: string
        completion_date: string
        coordinates: string
        cpo: string
        dist_gstin: null | string
        distributer: null | string
        hosting_address: string
        location: string
        pincode: number
        property_type: string
        station_type: string
        survey_ticket: number
    }
    ops_obj: OpsObj
    finance_obj: FinanceObj
}

export type TransporterObj = {
    gstin: string
    id: number
    name: string
    status: string
    transporter_id: string
}

type AddressObj = {
    city_id: number
    city_name: string
    hosting_address: string
    latitude: number
    longitude: number
    pincode: number
    site_id: string
    state_id: number
    state_name: string
}

export type DRResponse = {
    description: string
    item_id: number
    quantity: number
    remarks: string
    scope: string
    sku_code: string
}

type Items = {
    plp_id: number
    quantity: number
}

export type LogisticsTabData = {
    status: string
    status_id: number[]
    status_key: string
    ticket_count: number
    total_pages: number
}

export type LogisticsTabsResponse = {
    data: {
        message: LogisticsTabData[]
        status_code: number
        timestamp: string
    }
}

type ShipmentDetailResponse = {
    delivery_document: DeliveryDocument | null
    finance_id: number | null
    shipment_details: ShipmentDetails | null
    tentative_delivery_date: string | null
    transporter_id: number | null
    transporter_name: string | null
}

const handleSnackbar = async (
    queryFulfilled: any,
    successMsg: (data: any) => string,
    fallbackSuccess = "Success",
    fallbackError = "Something went wrong"
) => {
    try {
        const { data } = await queryFulfilled
        enqueueSnackbar(successMsg(data) || fallbackSuccess, { variant: "success" })
    } catch (error: any) {
        enqueueSnackbar(error?.error?.data?.message || error?.error?.data?.error || error?.message || fallbackError, {
            variant: "error",
        })
    }
}

// Every /download variant takes the same filters as the list itself; download_type is
// the only thing that changes, so the query string is built in one place.
export type LogisticsDownloadType = "list" | "details" | "line_item"

// line_item downloads answer with the S3 link inline; link is null when the filters
// matched nothing, and message carries the reason. The backend has since moved to the
// same file shape as the other exports (urls.download), so both are accepted.
export type LineItemDownloadResponse = {
    success?: boolean
    link?: string | null
    message?: string
    file_name?: string
    urls?: { download?: string; view?: string }
}

const buildLogisticsDownloadParams = (data: any, download_type: LogisticsDownloadType) => ({
    child_status_ids: JSON.stringify(data.statusId ?? []),
    assigned_to: data.assigned_to?.id ?? "",
    tag_ids: JSON.stringify(data.tags ?? []),
    transporter_id: data.delivery_vendor?.id ?? "",
    age: JSON.stringify(data.age ?? []),
    age_op: data.age_op ?? "",
    deal_type: data.dealType ?? "",
    ticket_raised_on_from: data.TicketStartDate ?? "",
    ticket_raised_on_to: data.TicketEndDate ?? "",
    product_ids: JSON.stringify((data.product_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))),
    search_key: data.search_key ?? "",
    raised_by_id: data.raised_by_id ?? "",
    read_filter: data.ReadUnread ?? "all",
    scope_of_work: data.sow ?? "",
    ticket_type: data.ticketType ?? "all",
    queryByIds: JSON.stringify(data.queryByIds ?? []),
    site_ids: data?.site_ids?.length ? JSON.stringify(data.site_ids.map((i: DropdownObj) => i.id)) : "[]",
    tentative_delivery_date_from: data.tentative_delivery_date_from ?? "",
    tentative_delivery_date_to: data.tentative_delivery_date_to ?? "",
    requested_delivery_date_from: data.requested_delivery_date_from ?? "",
    requested_delivery_date_to: data.requested_delivery_date_to ?? "",
    on_hold: data.on_hold?.id === 1 ? "yes" : data.on_hold?.id === 0 ? "no" : "",
    on_hold_date: data.on_hold_date ?? "",
    on_hold_reason_ids: JSON.stringify(data.on_hold_reason_ids ?? []),
    // Yes/No option -> 1/0; empty string leaves the filter unapplied.
    pod_uploaded: data.pod_uploaded?.id === 1 ? "1" : data.pod_uploaded?.id === 0 ? "0" : "",
    reject_tickets: data.reject_tickets ? "true" : "",
    // Several templates, but the endpoint kept the singular param name.
    template_id: JSON.stringify((data.template_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))),
    category_ids: JSON.stringify((data.category_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))),
    destination_site_ids: JSON.stringify(
        (data.destination_site_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
    ),
    source_site_ids: JSON.stringify((data.source_site_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))),
    // Complaint-linked dispatches. Applied to the tab counts, the list and every
    // export so a complaint filter narrows all of them consistently.
    complaint_ids: JSON.stringify((data.complaint_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))),
    // child_status ids of the DR's finance ticket. Informational filter: it
    // narrows the rows and the tab counts, but not the status tabs themselves.
    finance_status_ids: JSON.stringify(
        (data.finance_status_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
    ),
    parent_status: data.parent_status ?? "all",
    production_movement: data.production_movement ?? "",
    download_type,
})

const logisticsService = api.injectEndpoints({
    endpoints: build => ({
        getLogisticsTabs: build.query({
            query: params => ({
                url: `${MODULE_URL}/total`,
                params: {
                    child_status_ids: JSON.stringify(params.data.statusId ?? []),
                    assigned_to: params.data.assigned_to?.id ?? "",
                    tag_ids: JSON.stringify(params.data.tags ?? []),
                    transporter_id: params.data.delivery_vendor?.id ?? "",
                    age: JSON.stringify(params.data.age ?? []),
                    age_op: params.data.age_op ?? "",
                    deal_type: params.data.dealType ?? "",
                    ticket_raised_on_from: params.data.TicketStartDate ?? "",
                    ticket_raised_on_to: params.data.TicketEndDate ?? "",
                    product_ids: JSON.stringify(
                        (params.data.product_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    search_key: params.data.search_key ?? "",
                    raised_by_id: params.data.raised_by_id ?? "",
                    read_filter: params.data.ReadUnread ?? "all",
                    scope_of_work: params.data.sow ?? "",
                    ticket_type: params.data.ticketType ?? "all",
                    per_page: params?.selectedValue,
                    page: params?.page,
                    queryByIds: JSON.stringify(params.data.queryByIds ?? []),
                    site_ids: params?.data?.site_ids?.length
                        ? JSON.stringify(params.data.site_ids.map((i: DropdownObj) => i.id))
                        : "[]",
                    tentative_delivery_date_from: params.data.tentative_delivery_date_from ?? "",
                    tentative_delivery_date_to: params.data.tentative_delivery_date_to ?? "",
                    requested_delivery_date_from: params.data.requested_delivery_date_from ?? "",
                    requested_delivery_date_to: params.data.requested_delivery_date_to ?? "",
                    on_hold: params.data.on_hold?.id === 1 ? "yes" : params.data.on_hold?.id === 0 ? "no" : "",
                    on_hold_date: params.data.on_hold_date ?? "",
                    on_hold_reason_ids: JSON.stringify(params.data.on_hold_reason_ids ?? []),
                    // Yes/No option -> 1/0; empty string leaves the filter unapplied.
                    pod_uploaded:
                        params.data.pod_uploaded?.id === 1 ? "1" : params.data.pod_uploaded?.id === 0 ? "0" : "",
                    reject_tickets: params.data.reject_tickets ? "true" : "",
                    // Several templates, but the endpoint kept the singular param name.
                    template_id: JSON.stringify(
                        (params.data.template_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    category_ids: JSON.stringify(
                        (params.data.category_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    destination_site_ids: JSON.stringify(
                        (params.data.destination_site_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    source_site_ids: JSON.stringify(
                        (params.data.source_site_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    // Complaint-linked dispatches. Applied to the tab counts, the list and both
                    // exports so a complaint filter narrows all of them consistently.
                    complaint_ids: JSON.stringify(
                        (params.data.complaint_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    // child_status ids of the DR's finance ticket. Informational filter: it
                    // narrows the rows and the tab counts, but not the status tabs themselves.
                    finance_status_ids: JSON.stringify(
                        (params.data.finance_status_ids ?? []).map((i: any) =>
                            typeof i === "object" ? i.id : i
                        )
                    ),
                    // SB-7554: "dispatch" | "inward" inside the Production Planning tab; empty = no narrowing.
                    production_movement: params.data.production_movement ?? "",
                },
                useV3: true,
            }),
            transformResponse: (response: LogisticsTabsResponse) => response?.data?.message,
            providesTags: ["Logistics"],
        }),
        getLogisticsData: build.query<TicketDataResponseType, { data: any; selectedValue: number; page: number }>({
            query: params => {
                const queryParams = {
                    assigned_to: params.data.assigned_to?.id ?? "",
                    child_status_ids: JSON.stringify(params.data.statusId ?? []),
                    tag_ids: JSON.stringify(params.data.tags ?? []),
                    transporter_id: params.data.delivery_vendor?.id ?? "",
                    age: JSON.stringify(params.data.age ?? []),
                    age_op: params.data.age_op ?? "",
                    deal_type: params.data.dealType ?? "",
                    ticket_raised_on_from: params.data.TicketStartDate ?? "",
                    ticket_raised_on_to: params.data.TicketEndDate ?? "",
                    product_ids: JSON.stringify(
                        (params.data.product_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    search_key: params.data.search_key ?? "",
                    raised_by_id: params.data.raised_by_id ?? "",
                    read_filter: params.data.ReadUnread ?? "all",
                    scope_of_work: params.data.sow ?? "",
                    ticket_type: params.data.ticketType ?? "all",
                    per_page: params?.selectedValue,
                    page: params?.page,
                    queryByIds: JSON.stringify(params.data.queryByIds ?? []),
                    site_ids: params?.data?.site_ids?.length
                        ? JSON.stringify(params.data.site_ids.map((i: DropdownObj) => i.id))
                        : "[]",
                    tentative_delivery_date_from: params.data.tentative_delivery_date_from ?? "",
                    tentative_delivery_date_to: params.data.tentative_delivery_date_to ?? "",
                    requested_delivery_date_from: params.data.requested_delivery_date_from ?? "",
                    requested_delivery_date_to: params.data.requested_delivery_date_to ?? "",
                    on_hold: params.data.on_hold?.id === 1 ? "yes" : params.data.on_hold?.id === 0 ? "no" : "",
                    on_hold_date: params.data.on_hold_date ?? "",
                    on_hold_reason_ids: JSON.stringify(params.data.on_hold_reason_ids ?? []),
                    // Yes/No option -> 1/0; empty string leaves the filter unapplied.
                    pod_uploaded:
                        params.data.pod_uploaded?.id === 1 ? "1" : params.data.pod_uploaded?.id === 0 ? "0" : "",
                    reject_tickets: params.data.reject_tickets ? "true" : "",
                    // Several templates, but the endpoint kept the singular param name.
                    template_id: JSON.stringify(
                        (params.data.template_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    category_ids: JSON.stringify(
                        (params.data.category_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    destination_site_ids: JSON.stringify(
                        (params.data.destination_site_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    source_site_ids: JSON.stringify(
                        (params.data.source_site_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    // Complaint-linked dispatches. Applied to the tab counts, the list and both
                    // exports so a complaint filter narrows all of them consistently.
                    complaint_ids: JSON.stringify(
                        (params.data.complaint_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    // child_status ids of the DR's finance ticket. Informational filter: it
                    // narrows the rows and the tab counts, but not the status tabs themselves.
                    finance_status_ids: JSON.stringify(
                        (params.data.finance_status_ids ?? []).map((i: any) =>
                            typeof i === "object" ? i.id : i
                        )
                    ),
                    production_movement: params.data.production_movement ?? "",
                }

                return {
                    url: `${MODULE_URL}/${params.data.parent_status ?? "all"}`,
                    params: queryParams,
                    useV3: true,
                }
            },
            providesTags: ["Logistics"],
        }),
        getLogisticsTicketDetails: build.query<any, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/ticket_details_v3`,
                params: {
                    ticket_id,
                },
                useV3: true,
            }),
            providesTags: ["Logistics"],
            async onQueryStarted(params, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled // Get the response data
                    // Dispatch the data to the logistics slice to save it in Redux state
                    dispatch(setLogisticsDealDetailsData(data))
                } catch (error) {
                    console.error("Failed to fetch ticket details", error)
                }
            },
        }),
        getParsedDrData: build.query<any, { project_id: number; ticket_id: number; response_type?: string }>({
            query: args => ({
                url: `${MODULE_URL}/parsed_dr`,
                params: {
                    ...args,
                    response_type: "total",
                },
                useV3: true,
            }),
            providesTags: (result, error, args) => [{ type: "Logistics", id: args.ticket_id }],
        }),
        getLogisticsOtherDetails: build.query<IResponse<OtherDetailsResponseObj>, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/other_details`,
                params: {
                    ticket_id,
                },
            }),
            providesTags: (result, error, args) => [{ type: "Logistics", id: args.ticket_id }],
        }),
        getLogisticsTicketHistory: build.query<HistoryResponse, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/history`,
                params: {
                    ticket_id,
                },
                useV3: true,
            }),
            providesTags: (result, error, args) => [{ type: "Logistics", id: args.ticket_id }],
        }),
        getLogisticsButtonDropdown: build.query<IResponse<StatusButton[]>, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/button_dropdown`,
                params: {
                    ticket_id,
                },
                useV3: true,
            }),
            providesTags: (result, error, args) => [{ type: "Logistics", id: args.ticket_id }],
        }),
        getAllLogisticsTicketStatus: build.query<
            IResponse<{ id: number; name: string }>,
            { search_key: string } | void
        >({
            query: params => ({
                url: `${MODULE_URL}/statuses`,
                params: {
                    search_key: params?.search_key ?? "",
                },
            }),
            providesTags: ["Logistics"],
        }),
        getLogiticsChargerFlag: build.query<any, { ticket_id: string; project_id: string }>({
            query: params => ({
                url: `${MODULE_URL}/dispatch_charger_modal`,
                params: {
                    ticket_id: params.ticket_id,
                    project_id: params?.project_id || "",
                },
            }),
        }),
        getAllSiteIds: build.query<IResponse<SiteIdObj[]>, void>({
            query: () => ({
                url: `${MODULE_URL}/site_id`,
            }),
            providesTags: ["Projects", "Logistics"],
        }),
        getGsrTemplate: build.query<IFile, void>({
            query: () => ({
                url: `${MODULE_URL}/gsr_template`,
            }),
            transformResponse: (response: [IFile, number]) => response[0],
            providesTags: ["Settings"],
        }),
        getAllProjects: build.query<{ id: number; name: string }[], void>({
            query: () => ({
                url: `${MODULE_URL}/projects_list`,
                useV3: true
            }),
            transformResponse: (response: { id: number; name: string }[]) => response,
            providesTags: ["Projects"],
        }),
        getDeliveryVendors: build.query<TransporterObj[], void>({
            query: () => ({
                url: `${MODULE_URL}/delivery_vendors`,
            }),
            transformResponse: (response: IResponse<TransporterObj[]>) => response.data,
            providesTags: ["Transporters"],
        }),
        getLogisitcsFormAutofill: build.query<AddressObj, { id: number }>({
            query: ({ id }) => ({
                url: `${MODULE_URL}/form_autofill`,
                params: {
                    id,
                },
            }),
            transformResponse: (response: IResponse<AddressObj>) => response.data,
        }),
        downloadLogistic: build.query<IFile, { data: any }>({
            query: params => ({
                url: `${MODULE_URL}/download`,
                params: buildLogisticsDownloadParams(params.data, "details"),
                useV3: true,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, d => d?.data?.msg || "File Download Successfully")
            },
            keepUnusedDataFor: 0,
        }),
        downloadLogisticTickets: build.query<IFile, { data: any }>({
            query: params => ({
                url: `${MODULE_URL}/download`,
                params: buildLogisticsDownloadParams(params.data, "list"),
                useV3: true,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, d => d?.data?.msg || "File Download Successfully")
            },
            keepUnusedDataFor: 0,
        }),
        // One row per SKU / charger serial. Unlike list and details this one always answers
        // with a direct link, so there is no "sent to your email" path to handle.
        downloadLogisticLineItems: build.query<LineItemDownloadResponse, { data: any }>({
            query: params => ({
                url: `${MODULE_URL}/download`,
                params: buildLogisticsDownloadParams(params.data, "line_item"),
                useV3: true,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, d => d?.message || "File Download Successfully")
            },
            keepUnusedDataFor: 0,
        }),
        checkBulkUploadStatus: build.query<
            {
                alert: boolean
                msg: string
            },
            void
        >({
            query: () => ({
                url: `${MODULE_URL}/check_logistics_cost_bulk_update`,
            }),
            keepUnusedDataFor: 0,
        }),
        getShipmentDetail: build.query<ShipmentDetailResponse, { ticket_id: number }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/shipment_details?ticket_id=${ticket_id}`,
                useV3: true,
            }),
        }),
        downloadEcomLogsiticsTickets: build.query<IFile, { data: any }>({
            query: params => ({
                url: `${MODULE_URL}/ticket_list_download_ecom`,
                params: {
                    source_state_id: JSON.stringify(params.data.stateId ?? []),
                    destination_state_id: JSON.stringify(params.data.destinationstateId ?? []),
                    status_ids: JSON.stringify(params.data.statusId ?? []),
                    source_city_id: JSON.stringify(params.data.cityId ?? []),
                    destination_city_id: JSON.stringify(params.data.destinationcityId ?? []),
                    assigned_to: params.data.assigned_to?.id ?? "",
                    tag_ids: JSON.stringify(params.data.tags ?? []),
                    vendor_id: params.data.delivery_vendor?.id ?? "",
                    age: JSON.stringify(params.data.age ?? []),
                    age_op: params.data.age_op ?? "",
                    deal_type: params.data.dealType ?? "",
                    raised_on_end: params.data.TicketEndDate ?? "",
                    raised_on_start: params.data.TicketStartDate ?? "",
                    etd_start: params.data.ETDStartDate ?? "",
                    etd_end: params.data.ETDEndDate ?? "",
                    charger_type: JSON.stringify(params.data.chargerIds ?? []),
                    search_key: params.data.search_key ?? "",
                    raised_by_id: params.data.raised_by_id ?? "",
                    read_filter: params.data.ReadUnread ?? "all",
                    ticket_type: params.data.ticketType ?? "all",
                    scope_of_work: params.data.sow ?? "",
                    queryByIds: JSON.stringify(params.data.queryByIds ?? []),
                    site_ids: params?.data?.site_ids?.length
                        ? JSON.stringify(params.data.site_ids.map((i: DropdownObj) => i.id))
                        : "[]",
                    on_hold: params.data.on_hold?.id === 1 ? "yes" : params.data.on_hold?.id === 0 ? "no" : "",
                    on_hold_date: params.data.on_hold_date ?? "",
                    on_hold_reason_ids: JSON.stringify(params.data.on_hold_reason_ids ?? []),
                    // Yes/No option -> 1/0; empty string leaves the filter unapplied.
                    pod_uploaded:
                        params.data.pod_uploaded?.id === 1 ? "1" : params.data.pod_uploaded?.id === 0 ? "0" : "",
                    reject_tickets: params.data.reject_tickets ? "true" : "",
                    ...(params.data?.apply_filters && { apply_filters: params.data.apply_filters }),
                    status_id: params.data.status_id,
                    parent_status: params.data.parent_status ?? "all",
                },
                useV3: true,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, d => d?.data?.msg || "File Download Successfully")
            },
            keepUnusedDataFor: 0,
        }),
        getLogisticTicketDetails: build.query<TicketDetails[], { ticket_id: number }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/${ticket_id}/tickets`,
                useV3: true,
            }),
            transformResponse: (response: TicketDetails | TicketDetails[]) => {
                return Array.isArray(response) ? response : [response]
            },
        }),
        // Mutations
        addSoloLogisticsTicket: build.mutation<IResponse<string>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/ticket`,
                method: "POST",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: "Ticket Created!" }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message }))
                }
            },
            invalidatesTags: ["Logistics"],
        }),
        // Solo logistics tickets hang off a hidden project that never shows in the projects
        // module, so the category is edited from the ticket itself. Rejected with 400 on an
        // associated ticket — there the project is visible and owns the edit.
        updateLogisticsTicketCategory: build.mutation<
            { status: string; ticket_id: number; category: { id: number; name: string } },
            { ticket_id: number | string; category_id: number }
        >({
            query: ({ ticket_id, category_id }) => ({
                url: `${MODULE_URL}/${ticket_id}/category`,
                method: "PUT",
                body: { category_id },
                useV3: true,
            }),
            invalidatesTags: ["Logistics"],
        }),
        raiseLogisticsTicket: build.mutation<
            { add_payment_terms: boolean; finance_id: number; project_id: number; status: string; ticket_id: number },
            any
        >({
            query: body => ({
                url: `${MODULE_URL}/raise_solo`,
                method: "POST",
                body: body,
                useV3: true,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled
                    enqueueSnackbar("Ticket Raised Successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.message, { variant: "error" })
                }
            },
            invalidatesTags: ["Logistics"],
        }),
        editSoloLogisticsTicket: build.mutation<IResponse<string>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/edit_solo_ticket`,
                method: "PUT",
                body: body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message }))
                }
            },
            invalidatesTags: ["Logistics"],
        }),
        linkSoloLogisticsTicket: build.mutation<{ msg: string }, FormData>({
            query: body => ({
                url: `${MODULE_URL}/link_solo_log`,
                method: "PUT",
                useV3: true,
                body: body,
            }),
            onQueryStarted: async (arg, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(`${data?.msg}`, { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(`${error?.error?.data?.error || error?.message}`, { variant: "error" })
                }
            },
            invalidatesTags: ["Logistics"],
        }),
        editLogisticsTicketStatus: build.mutation<{ message: string }, FormData>({
            query: body => ({
                url: `${MODULE_URL}/update_status`,
                method: "PUT",
                body: body,
                useV3: true,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(`${data?.message}`, { variant: "success" })
                } catch (error: any) {
                    // queryFulfilled rejects with { error: { status, data } }: read the backend's own message
                    // (e.g. a Production Planning refusal) instead of showing "undefined".
                    enqueueSnackbar(apiErrorMessage(error, "Could not update the status"), { variant: "error" })
                }
            },
            invalidatesTags: ["Logistics", "Installation"],
        }),
        editDCDetail: build.mutation<IResponse<unknown>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/ticket_details`,
                method: "PUT",
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Logistics", id: arg.get("ticket_id") as string },
                "Logistics",
            ],
        }),
        dropSoloLogisticsTicket: build.mutation<IResponse<string>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/drop_solo_ticket`,
                method: "PUT",
                body: body,
            }),
            async onQueryStarted(params, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(
                        setAlertFunc({
                            type: "success",
                            msg: data?.data,
                        })
                    )
                } catch (error: any) {
                    dispatch(
                        setAlertFunc({
                            type: "error",
                            msg: error?.message,
                        })
                    )
                    console.error("Failed to drop ticket", error)
                }
            },
            invalidatesTags: ["Logistics"],
        }),
        logisticsSkipToStatus: build.mutation<IResponse<{ message: string }>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/skip_to_status`,
                method: "PUT",
                body: body,
            }),
            async onQueryStarted(params, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(
                        setAlertFunc({
                            type:
                                data.status_code === 200 && data.data.message !== "Access denied" ? "success" : "error",
                            msg: data?.data?.message,
                        })
                    )
                } catch (error) {
                    console.error("Failed to fetch ticket details", error)
                }
            },
            invalidatesTags: ["Logistics"],
        }),
        // Mutation to add Purchase orders
        addLogisticsTicketPurchaseOrder: build.mutation<IResponse<[string]>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/ticket_details`,
                method: "POST",
                body: body,
            }),
            async onQueryStarted(params, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(
                        setAlertFunc({
                            type: "success",
                            msg: data?.data[0],
                        })
                    )
                } catch (error) {
                    console.error("Failed to fetch ticket details", error)
                }
            },
            invalidatesTags: (result, error, args) => [
                { type: "Logistics", id: args.get("ticket_id") as string },
                "Logistics",
            ],
        }),
        // Mutation to delete Purchase orders
        deleteLogisticsTicketPurchaseOrder: build.mutation<
            IResponse<[string]>,
            { ticket_id: string; purchase_orders: number }
        >({
            query: params => ({
                url: `${MODULE_URL}/ticket_details`,
                method: "DELETE",
                params: params,
            }),
            async onQueryStarted(params, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(
                        setAlertFunc({
                            type: "success",
                            msg: data?.data[0],
                        })
                    )
                } catch (error) {
                    console.error("Failed to fetch ticket details", error)
                }
            },
            invalidatesTags: (result, error, args) => [{ type: "Logistics", id: args.ticket_id }],
        }),

        updateLogisticsTotalCost: build.mutation<IResponse<string>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/ticket_details`,
                method: "PUT",
                body: body,
            }),
            invalidatesTags: (result, error, args) => [
                { type: "Logistics", id: args.get("ticket_id") as string },
                "Logistics",
            ],
        }),
        editPODImages: build.mutation<IResponse<string>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/ticket_details_v3`,
                method: "PUT",
                body: body,
                useV3: true,
            }),
            invalidatesTags: (result, error, args) => [
                { type: "Logistics", id: args.get("ticket_id") as string },
                "Logistics",
            ],
        }),
        parseDr: build.mutation<DRResponse[], FormData>({
            query: body => ({
                url: `${MODULE_URL}/parse_dr`,
                method: "POST",
                body: body,
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                } catch (error: any) {
                    dispatch(
                        setAlertFunc({
                            type: "error",
                            msg: error?.error?.data?.error || "Something went wrong!",
                        })
                    )
                    console.error("Failed to fetch ticket details", error)
                }
            },
        }),
        raiseSoloLogistics: build.mutation<
            {
                add_payment_terms: boolean
                finance_id: number
                id: number
                msg: string
            },
            FormData
        >({
            query: body => ({
                url: `${MODULE_URL}/raise_solo_logistics`,
                useV3: true,
                method: "POST",
                body,
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(
                        setAlertFunc({
                            type: "success",
                            msg: data?.msg,
                        })
                    )
                } catch (error) {
                    console.error("Failed to fetch ticket details", error)
                }
            },
            invalidatesTags: ["Logistics"],
        }),
        validateLinking: build.query<
            { flag: boolean; display_id?: string; id?: number },
            { project_id: string; ticket_id: string }
        >({
            query: ({ project_id, ticket_id }) => ({
                url: `${MODULE_URL}/validate_linking`,
                method: "GET",
                useV3: true,
                params: { project_id, ticket_id },
            }),
        }),
        // FormData -> fileName
        addBulkUpdateCost: build.mutation<{ msg: string }, FormData>({
            query: body => ({
                url: `${MODULE_URL}/add_logistics_cost_in_bulk`,
                method: "PUT",
                body: body,
            }),
            invalidatesTags: ["Logistics"],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.msg }))
                } catch (error: any) {
                    dispatch(
                        setAlertFunc({ type: "error", msg: error?.error?.data?.message || "Something went wrong!" })
                    )
                }
            },
        }),
        createSplitDelivery: build.mutation<any, { ticket_id: number; items: Items[]; clearCache?: boolean }>({
            query: ({ clearCache = true, ...body }) => ({
                url: `${MODULE_URL}/split_delivery`,
                method: "POST",
                useV3: true,
                body: body,
            }),
            invalidatesTags: (_, __, arg) => (arg.clearCache ? ["Logistics"] : []),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(`${data?.message}. New Ticket ID: ${data.new_ticket_id}`, { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(`${error?.error?.data?.message || "Something went wrong!"}`, { variant: "error" })
                }
            },
        }),
        saveShipmentDetail: build.mutation<any, ShipmentPayload>({
            query: body => ({
                url: `${MODULE_URL}/save_shipment_details`,
                method: "POST",
                useV3: true,
                body: body,
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(`${data?.message}`, { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(`${error?.error?.data?.error || "Something went wrong!"}`, { variant: "error" })
                }
            },
        }),
        editTicketDetails: build.mutation<any, Record<string, unknown>>({
            query: body => ({
                url: `${MODULE_URL}/update_ticket`,
                method: "PUT",
                useV3: true,
                body: body,
            }),
            invalidatesTags: ["Logistics"],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(`${data?.message}`, { variant: "success" })
                } catch (error: any) {
                    // update_ticket answers refusals with { error } (SB-7554 409/422), other failures with { message }
                    enqueueSnackbar(apiErrorMessage(error, "Something went wrong!"), { variant: "error" })
                }
            },
        }),
        editMaterialDetails: build.mutation<any, Record<string, unknown>>({
            query: body => ({
                url: `${MODULE_URL}/update_ticket_materials`,
                method: "PUT",
                useV3: true,
                body: body,
            }),
            invalidatesTags: (result, error, arg) => [{ type: "Logistics", id: arg.ticket_id as number }],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(`${data?.message}`, { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(`${error?.error?.data?.error || "Something went wrong!"}`, { variant: "error" })
                }
            },
        }),
        editLogisticsHoldStatus: build.mutation({
            query: body => ({
                url: `ticket_on_hold`,
                method: "POST",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Logistics", "Approvals"],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.message }))
                } catch (error: any) {
                    const error_text =
                        error?.error?.data?.error ||
                        error?.error?.data?.message ||
                        error?.message ||
                        error?.error ||
                        "Something went wrong!"
                    dispatch(setAlertFunc({ type: "error", msg: error_text }))
                }
            },
        }),
        bulkUpdateStatus: build.mutation<{ message: string }, FormData>({
            query: body => ({
                url: `${MODULE_URL}/logistics_bulk_update_status`,
                method: "PUT",
                body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: ["Logistics"],
        }),
        raiseLinkedLogistics: build.mutation<
            any,
            {
                project_id: number
                contains_rfid: 0 | 1
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/raise_linked_logistics`,
                method: "POST",
                useV3: true,
                body,
            }),
            invalidatesTags: ["Logistics", "Projects"],
            async onQueryStarted(_, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(`${data?.message}`, { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(`${error?.error?.data?.error || "Something went wrong!"}`, { variant: "error" })
                }
            },
        }),
    }),
})

export const {
    useLazyGetLogisticsTabsQuery,
    useLazyGetLogisticsDataQuery,
    useGetLogisticsDataQuery,
    useLazyGetAllLogisticsTicketStatusQuery,
    useLazyGetLogisitcsFormAutofillQuery,
    useCheckBulkUploadStatusQuery,
    useGetLogisticsTicketDetailsQuery,
    useGetLogisticsOtherDetailsQuery,
    useGetLogisticsTicketHistoryQuery,
    useGetLogisticsButtonDropdownQuery,
    useGetAllSiteIdsQuery,
    useGetGsrTemplateQuery,
    useGetAllProjectsQuery,
    useGetLogiticsChargerFlagQuery,
    useGetDeliveryVendorsQuery,
    useLazyValidateLinkingQuery,
    useGetParsedDrDataQuery,
    useLazyGetShipmentDetailQuery,
    useLazyDownloadEcomLogsiticsTicketsQuery,
    useGetLogisticTicketDetailsQuery,

    useAddSoloLogisticsTicketMutation,
    useRaiseLogisticsTicketMutation,
    useUpdateLogisticsTicketCategoryMutation,
    useEditSoloLogisticsTicketMutation,
    useLinkSoloLogisticsTicketMutation,
    useEditLogisticsTicketStatusMutation,
    useDropSoloLogisticsTicketMutation,
    useLogisticsSkipToStatusMutation,
    useEditDCDetailMutation,
    useAddLogisticsTicketPurchaseOrderMutation,
    useDeleteLogisticsTicketPurchaseOrderMutation,
    useUpdateLogisticsTotalCostMutation,
    useEditPODImagesMutation,
    useParseDrMutation,
    useRaiseSoloLogisticsMutation,
    useLazyDownloadLogisticTicketsQuery,
    useLazyDownloadLogisticQuery,
    useLazyDownloadLogisticLineItemsQuery,
    useAddBulkUpdateCostMutation,
    useCreateSplitDeliveryMutation,
    useSaveShipmentDetailMutation,
    useEditTicketDetailsMutation,
    useEditMaterialDetailsMutation,
    useEditLogisticsHoldStatusMutation,
    useRaiseLinkedLogisticsMutation,
    useBulkUpdateStatusMutation,
} = logisticsService

export { logisticsService }
